"use client";
import { PropsWithChildren, useEffect, useState } from "react";
import { getEnv } from "@/env";
import Base64Encode from "@/components/Base64Encode";
import { Skeleton } from "@/components/ui/skeleton";
import { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { sendTaxiFormEvent, TaxiFormSegmentEventData } from "@/lib/segment";
import {
  LeadFormEventListeners,
  LeadFormPluginOptions,
} from "@/types/LeadFormPlugin";
import { validateLocation } from "@/utils/functions/validateLocation";
import { getCorrelationID } from "@/utils/tagular/helpers";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import type { FormData } from "./types";
import isExternalLink from "@/utils/functions/isExternalLink";
import { getCrossSellThankYouUrl, shouldUseCrossSellUrl } from "./utils";
import { modifyDegreePostSubmitUrl } from "./modifyDegreePostSubmitUrl";
import { injectStartTimeframeRouting } from "./startTimeframeParam";
import { usePathname } from "next/navigation";
import { evaluateFeatureFlag } from "@/lib/feature-flags/evaluateFeatureFlag";
import useDegreeTYPage from "@/monarch/rules/degreeTYPage/useDegreeTYPage";

type FormProductData = {
  brand?: string;
  category?: string;
  productvariant?: string;
  name?: string;
  productUuid?: string;
};

type CohesionProductData = FormProductData & {
  action: string;
  customName?: string;
};

const NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED =
  "NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED";
const crossSellDefaultFlag =
  getEnv(NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED) ?? "false";

const formSkeletonId = "taxi_form_footer_loading";
const formContainerId = "taxi_form_internal_container";

export const TaxiFormLoadingSkeleton = ({ formSalt }: { formSalt: string }) => (
  <div className="space-y-4" id={`${formSalt}_${formSkeletonId}`}>
    <div className="space-y-2 mb-20">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
    <Skeleton className="h-10 w-full" />
  </div>
);

export type UseTaxiFormProps = {
  formGrouping: string;
  formId: string;
  postSubmitUrl: string;
  salt?: string;
  taxiFormData?: FormData | null;
  eventListeners?: LeadFormEventListeners;
  isLoading?: boolean;
  formClass?: string;
  segmenteventdata?: TaxiFormSegmentEventData;
  productData: FormProductData;
  showLoading?: boolean;
  allowedIn?: string[];
  blockedIn?: string[];
  customName?: string;
};

const generateCohesionPayload = ({
  action,
  brand,
  category,
  productvariant,
  name,
  productUuid,
  customName,
}: CohesionProductData) =>
  ({
    correlation: {
      id: getCorrelationID(),
    },
    metadata: {
      category: "Lead",
      action,
      timestamp: new Date().toISOString(),
      customName,
      productList: [
        {
          brand: brand ?? "",
          variant: translateVariant(productvariant ?? ""),
          category: category,
          name: name,
          id: productUuid,
        },
      ],
    },
  }) as ConversionTracked;

const trackNextStep = (productData: FormProductData, customName?: string) => {
  const payload = generateCohesionPayload({
    ...productData,
    action: "Started",
    customName,
  });

  const event = makeEvent<ConversionTracked, EVENTS>(payload);
  event<ConversionTracked>(EVENTS.ConversionTracked)();
};

const trackSubmitted = (
  segmenteventdata?: TaxiFormSegmentEventData,
  productData?: FormProductData,
  customName?: string,
) => {
  const payload = generateCohesionPayload({
    ...productData,
    action: "Submitted",
    customName,
  });

  const event = makeEvent<ConversionTracked, EVENTS>(payload);
  event<ConversionTracked>(EVENTS.ConversionTracked)();

  if (segmenteventdata) {
    sendTaxiFormEvent(segmenteventdata);
  }
};

export function hideLoadingSkeleton(showLoading: boolean, salt: string) {
  // Taxi LeadFormPlugin cannot hanlde react state changes, so we need to resort to vanillajs
  if (showLoading) {
    document
      .getElementById(`${salt}_${formSkeletonId}`)
      ?.classList.add("hidden");
    document
      .getElementById(`${salt}_${formContainerId}`)
      ?.classList.remove("hidden");
  }
}

export default function useTaxiForm({
  formGrouping,
  formId,
  postSubmitUrl,
  salt = "___",
  taxiFormData,
  eventListeners,
  isLoading,
  formClass,
  segmenteventdata,
  productData,
  showLoading = false,
  allowedIn = [],
  blockedIn = [],
  customName,
}: UseTaxiFormProps) {
  const [isTaxiScriptLoaded, setIsTaxiScriptLoaded] = useState(false);
  const [crossSellEnabled, setCrossSellEnabled] = useState(false);
  const {
    redirectToPartnerTyPage,
    routeFlag,
    isLoading: isDegreeTYPageLoading,
  } = useDegreeTYPage();
  const resolvedFormId = formId;
  const packageFromId = `${salt}-${formGrouping}-${resolvedFormId}-full-taxi-lead-form-package`;
  const templateFromId = `${salt}-lead-form-template`;
  const mountPointId = `${salt}-taxi-plugin-mount-point`;
  const titleId = `${salt}-title`;
  const introTextId = `${salt}-intro_text`;
  const progressBarId = `${salt}-lead-form-progress-bar-mount-point`;
  const errorMountPointId = `${salt}-lead-form-error-mount-point`;
  const fieldMountPointId = `${salt}-lead-form-field-mount-point`;
  const nextActionButtonId = `${salt}-lead-form-next-action-button`;
  const footerMountPointId = `${salt}-lead-form-footer-mount-point`;

  const pathname = usePathname();

  let hasExternalReferrer = false;
  if (typeof document !== "undefined") {
    hasExternalReferrer =
      !document.referrer || isExternalLink(document.referrer);
  }
  // Degree pages should always show taxi form on first site visit, regardless of location
  const ignoreLocation =
    hasExternalReferrer &&
    productData?.productvariant &&
    ["Masters", "Bachelors", "Doctorate", "Certificate"].includes(
      productData.productvariant,
    );

  const isValidLocation = ignoreLocation
    ? true
    : validateLocation(allowedIn, blockedIn);

  // Check if cross-sell feature flag is enabled
  useEffect(() => {
    const checkCrossSellFlag = async () => {
      try {
        const flagValue = await evaluateFeatureFlag(
          NEXT_PUBLIC_FEATURE_DEGREE_TY_PAGE_ENABLED,
          crossSellDefaultFlag,
        );
        // still controlled only by feature flag unless redirectToPartnerTyPage is defined
        const enableCrossSell =
          flagValue === "true" &&
          (typeof redirectToPartnerTyPage === "boolean"
            ? redirectToPartnerTyPage === false
            : true);
        setCrossSellEnabled(enableCrossSell);
      } catch (error) {
        setCrossSellEnabled(false);
      }
    };

    checkCrossSellFlag();
  }, [redirectToPartnerTyPage]);

  const shouldUseCrossSell =
    crossSellEnabled &&
    shouldUseCrossSellUrl(
      productData?.brand || "",
      productData?.productvariant || "",
    );

  const finalPostSubmitUrl = shouldUseCrossSell
    ? getCrossSellThankYouUrl(productData?.brand || "", pathname, postSubmitUrl)
    : modifyDegreePostSubmitUrl(postSubmitUrl, routeFlag);

  // On the RV TYP flow, route accelerated-start leads to the `at=true` variant via
  // the plugin's native `thankYouPageURLs` map. No-op for control traffic.
  const taxiFormDataForPlugin =
    routeFlag && !shouldUseCrossSell
      ? injectStartTimeframeRouting(taxiFormData, finalPostSubmitUrl)
      : taxiFormData;

  // Initialize TaxiForm once script is loaded and dependencies are ready
  useEffect(() => {
    if (isLoading || !isTaxiScriptLoaded) return;
    if (isDegreeTYPageLoading) return;
    if (!taxiFormData || !isValidLocation) return;

    const options: LeadFormPluginOptions = {
      salt: salt,
      thankYouURL: finalPostSubmitUrl,
      packageType: "element",
      packageFrom: packageFromId,
      templateType: "element",
      templateFrom: templateFromId,
      telemetryEnabled: false,
      hostPresetFields: {
        ip: "", // window.visitor?.ip?.address || "0.0.0.0",
        lead_source: "edx",
        // page_url: pageUrlWithEAID,
        eaid: "",
        rv_source: "organic",
        // splash_creative: '', // TODO: figure out how to supply this
      },
    };

    // Event should only fire on the first instance of Next Step
    let hasFirstStepRun = false;

    options.engineEventListeners = {
      nextScreenTransitionSucceeded: () => {
        if (!hasFirstStepRun) {
          hasFirstStepRun = true;
          trackNextStep(productData, customName);
        }
      },
      imqSubmissionAttempt: () => {
        trackSubmitted(segmenteventdata, productData, customName);
      },
      footerContentRendered: () => {
        hideLoadingSkeleton(showLoading, salt);
      },
      noFooterContentRendered: () => {
        hideLoadingSkeleton(showLoading, salt);
      },
    };

    if (eventListeners) {
      options.engineEventListeners = {
        ...options.engineEventListeners,
        ...eventListeners,
      };
    }

    const taxiForm = new window.LeadFormPlugin(
      window.document,
      mountPointId,
      options,
    );
    taxiForm.start();

    // LeadFormPlugin mutates the progressbar element's attributes at runtime,
    // discarding aria-label from the HTML template. Re-apply it via
    // MutationObserver so screen readers always get an accessible name.
    const mountEl = document.getElementById(mountPointId);
    let progressObserver: MutationObserver | undefined;
    if (mountEl) {
      const patchProgressbar = () => {
        const bar = mountEl.querySelector<HTMLElement>('[role="progressbar"]');
        if (bar && !bar.getAttribute("aria-label")) {
          bar.setAttribute("aria-label", "Application progress");
        }
      };
      progressObserver = new MutationObserver(patchProgressbar);
      progressObserver.observe(mountEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-label"],
      });
      patchProgressbar();
    }

    //fallback in case no event to remove the loading is triggered
    if (showLoading) {
      setTimeout(() => {
        hideLoadingSkeleton(showLoading, salt);
      }, 3000);
    }

    return () => progressObserver?.disconnect();
  }, [
    isTaxiScriptLoaded,
    mountPointId,
    packageFromId,
    postSubmitUrl,
    salt,
    templateFromId,
    taxiFormData,
    isLoading,
    isValidLocation,
    eventListeners,
    showLoading,
    productData,
    segmenteventdata,
    routeFlag,
    isDegreeTYPageLoading,
  ]);

  // Check if script is already loaded in window when component mounts
  useEffect(() => {
    const checkInterval = 300;
    const maxPollingTime = 30000;

    const intervalId = setInterval(() => {
      if (typeof window !== "undefined" && window.LeadFormPlugin) {
        setIsTaxiScriptLoaded(true);
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      }
    }, checkInterval);

    const timeoutId = setTimeout(() => {
      window.newrelic?.noticeError("TaxiForm script failed to load", {
        formId: resolvedFormId,
      });
      clearInterval(intervalId);
    }, maxPollingTime);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [resolvedFormId]);

  const TaxiForm = ({ children }: PropsWithChildren) => {
    if (!taxiFormData || isLoading || !isValidLocation) return null;

    return (
      <>
        {showLoading ? <TaxiFormLoadingSkeleton formSalt={salt} /> : null}
        <section className="taxi-form-section" data-testid="taxi-form">
          <div className={`taxi-form ${formClass || ""}`}>
            <pre id={packageFromId} className="hidden">
              {JSON.stringify(taxiFormDataForPlugin)}
            </pre>
            <div
              id={mountPointId}
              data-package-type="element"
              data-template-type="element"
            />
          </div>
          <div
            id={templateFromId}
            className="hidden"
            aria-hidden="true"
            data-encoded="base64"
          >
            <Base64Encode>
              <div
                className={showLoading ? "hidden" : ""}
                id={`${salt}_${formContainerId}`}
              >
                {children}
              </div>
            </Base64Encode>
          </div>
        </section>
      </>
    );
  };

  return {
    TaxiForm,
    templateIds: {
      titleId,
      introTextId,
      progressBarId,
      errorMountPointId,
      fieldMountPointId,
      nextActionButtonId,
      footerMountPointId,
    },
    hideLoadingSkeleton,
  };
}
